import React from "react";
import Modal from "react-bootstrap/Modal";
import divWithClassName from "react-bootstrap/divWithClassName";
import styles from "./QQModal.module.scss";
import clsx from "clsx";

export interface QQModalProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  show: boolean;
  closeButton?: boolean;
  contentClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  size?: "sm" | "lg" | "xl";
  /**
   * Footer content rules:
   *  - `false` / `null` / `undefined` → no footer rendered.
   *  - `true` → renders the legacy default "Ok" button that calls onClose.
   *  - any ReactNode → renders that node inside the footer (caller owns the buttons).
   *
   * The boolean=true behavior is preserved for backwards compatibility with
   * existing call sites that did `footer={true}`.
   */
  footer?: React.ReactNode | boolean;
  onClose?: () => void;
  backdrop?: boolean | "static";
  centered?: boolean;
}

export const QQModal: React.FC<QQModalProps> = ({
  title,
  show,
  closeButton,
  contentClassName,
  headerClassName,
  titleClassName,
  bodyClassName,
  footerClassName,
  size,
  footer = null,
  children,
  onClose = () => {},
  backdrop,
  centered,
}) => {
  const onHide = () => {
    if (closeButton) {
      onClose();
    }
  };

  const renderFooter = () => {
    if (!footer) return null;

    // Legacy: `footer={true}` → render the default Ok button.
    if (footer === true) {
      return (
        <Modal.Footer className={footerClassName}>
          <span className="d-inline-block">
            <button
              className={clsx(styles.modalFooter, "font-size-12 m-0 pe-100 ps-100 btn btn-primary")}
              type="button"
              onClick={onHide}
            >
              Ok
            </button>
          </span>
        </Modal.Footer>
      );
    }

    // Caller-provided node — render it directly.
    return <Modal.Footer className={footerClassName}>{footer}</Modal.Footer>;
  };

  return (
    <Modal
      {...{ show, size, onHide, backdrop, centered }}
      contentClassName={clsx(styles.borderTop, styles.bottomRounded, contentClassName)}
    >
      <Modal.Header
        bsPrefix={styles.modalheader}
        className={clsx(
          headerClassName,
          "d-flex align-items-center justify-content-between py-8 px-10",
        )}
        closeButton={closeButton}
      >
        <Modal.Title as={divWithClassName(clsx("p-0", titleClassName))}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body as={divWithClassName(clsx(styles.bottomRounded, "p-0", bodyClassName))}>
        {children}
      </Modal.Body>
      {renderFooter()}
    </Modal>
  );
};
